import ChatProposalApplyOutcome from "@mapmycustomers/shared/enum/chat/ChatProposalApplyOutcome";
import ChatProposalStatus from "@mapmycustomers/shared/enum/chat/ChatProposalStatus";
import { ChatProposal } from "@mapmycustomers/shared/types/chat/ChatContent";
import { ChatContextEntityType } from "@mapmycustomers/shared/types/chat/ChatMessage";
import { FieldAgentApplyFieldError } from "@mapmycustomers/shared/types/chat/FieldAgentChat";
import { AnyEntityId } from "@mapmycustomers/shared/types/entity";

// The apply outcomes that settle a row for good, and so override the wire status — which stays
// frozen at whatever it was when the message arrived. `invalid` is deliberately absent: that one is
// retryable once the rep fixes the value. `conflict` is here but NOT in the reducer's
// TERMINAL_OUTCOMES — it settles the row, yet the row still needs its freshValues and draft to
// render. The two lists answer different questions and conflict is the case that separates them.
export type ChatProposalStatusOverride =
  | ChatProposalApplyOutcome.APPLIED
  | ChatProposalApplyOutcome.CANCELLED
  | ChatProposalApplyOutcome.CONFLICT
  | ChatProposalApplyOutcome.EXPIRED
  | ChatProposalApplyOutcome.FAILED
  | ChatProposalApplyOutcome.STALE;

// Two disjoint halves, which is why nothing here is required: the failure fields describe an apply
// that did not go through, the created-record pair an apply that did. No outcome ever carries both.
export interface ChatProposalOutcomeDetail {
  createdEntityId?: AnyEntityId;
  createdEntityType?: ChatContextEntityType;
  failureReason?: string;
  fieldErrors?: FieldAgentApplyFieldError[];
  freshValues?: Record<string, unknown>;
}

export interface ChatProposalUiState {
  checked?: boolean; // undefined = default = checked
  draft?: Record<string, unknown>;
  hidden?: boolean; // not_found: the row is dropped, not decorated
  outcome?: ChatProposalOutcomeDetail; // invalid keeps the row pending; conflict settles it
  statusOverride?: ChatProposalStatusOverride;
}

// What a row renders, spelled out by where each value comes from: the five BE puts on the proposal
// itself, `stale` which only an apply attempt can reveal, and `unknown` which is ours alone — the
// locked rendering for a proposal we cannot place.
export type ChatProposalRowStatus =
  | "unknown"
  | ChatProposalApplyOutcome.CONFLICT
  | ChatProposalApplyOutcome.STALE
  | ChatProposalStatus;

export const PROPOSAL_ROW_STATUS_UNKNOWN = "unknown" as const;

// The only place the status is actually checked, which is why it is checked here rather than in the
// parser: parseChatResponse asserts the type, and chatConversationStorage restores proposals a
// previous build persisted without re-validating anything. A value that satisfies neither path still
// has to render as something.
const PROPOSAL_STATUSES = Object.values(ChatProposalStatus);

// The one seam between the two vocabularies. Total, not Partial, on purpose: adding a sixth member
// to ChatProposalStatusOverride fails to compile until its rendering is decided here, which is the
// property that makes this map worth more than the array-and-includes it replaced.
const ROW_STATUS_BY_OVERRIDE: Record<ChatProposalStatusOverride, ChatProposalRowStatus> = {
  [ChatProposalApplyOutcome.APPLIED]: ChatProposalStatus.APPLIED,
  [ChatProposalApplyOutcome.CANCELLED]: ChatProposalStatus.CANCELLED,
  [ChatProposalApplyOutcome.CONFLICT]: ChatProposalApplyOutcome.CONFLICT,
  [ChatProposalApplyOutcome.EXPIRED]: ChatProposalStatus.EXPIRED,
  [ChatProposalApplyOutcome.FAILED]: ChatProposalStatus.FAILED,
  [ChatProposalApplyOutcome.STALE]: ChatProposalApplyOutcome.STALE,
};

export const resolveProposalRowStatus = (
  proposal: ChatProposal,
  ui: ChatProposalUiState | undefined,
  now: number
): ChatProposalRowStatus => {
  if (ui?.statusOverride) {
    // Falls back rather than trusting the index: the map is total over the type, but
    // chatConversationStorage restores proposalUi without re-validating it, so a stale override
    // written by an earlier build reaches here as a value the type says cannot exist. Locking the
    // row is the same answer an unplaceable wire status gets below.
    return ROW_STATUS_BY_OVERRIDE[ui.statusOverride] ?? PROPOSAL_ROW_STATUS_UNKNOWN;
  }
  const wire = PROPOSAL_STATUSES.find((candidate) => candidate === proposal.status);
  if (!wire) {
    return PROPOSAL_ROW_STATUS_UNKNOWN; // a status this build cannot place renders locked
  }
  if (wire !== ChatProposalStatus.PENDING) {
    return wire;
  }
  const expiresAt = Date.parse(proposal.expiresAt);
  // An unparseable expiresAt can't tell "still pending" from "already lapsed" — locking it as
  // unknown (rather than defaulting to pending) is what keeps the NaN out of every downstream
  // countdown and Math.min that assumes a real timestamp.
  if (!Number.isFinite(expiresAt)) {
    return PROPOSAL_ROW_STATUS_UNKNOWN;
  }
  return expiresAt <= now ? ChatProposalStatus.EXPIRED : ChatProposalStatus.PENDING;
};

export const isProposalChecked = (ui: ChatProposalUiState | undefined): boolean =>
  ui?.checked !== false;

// Whether a row still renders the rep's draft, or falls back to what the agent originally proposed.
// The question is only ever "was that draft submitted?" — a draft the rep never got to send says
// nothing about what became of the proposal, and showing it under a "Rejected" icon reads as though
// those values were the ones acted on.
//
// Total, not Partial: a new row status has to decide this here, because the wrong default shows an
// edit that never left the client as if the platform had seen it.
const DRAFT_RENDERED_BY_ROW_STATUS: Record<ChatProposalRowStatus, boolean> = {
  [ChatProposalApplyOutcome.CONFLICT]: true,
  [ChatProposalApplyOutcome.STALE]: false,
  [ChatProposalStatus.APPLIED]: true,
  [ChatProposalStatus.CANCELLED]: false,
  [ChatProposalStatus.EXPIRED]: false,
  [ChatProposalStatus.FAILED]: true,
  // Also covers `invalid`, which keeps the row pending: there the draft is what will be sent next.
  [ChatProposalStatus.PENDING]: true,
  [PROPOSAL_ROW_STATUS_UNKNOWN]: false,
};

export const shouldRenderProposalDraft = (status: ChatProposalRowStatus): boolean =>
  DRAFT_RENDERED_BY_ROW_STATUS[status];
